import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StepConditionEditorComponent } from '../step-condition-editor/step-condition-editor.component';
import {
  activeRowCount,
  countSelectedRows,
  missingRowNumbers,
  parseFilterExpression,
} from '../../shared/data-sheet-filters.codec';
import { tdbRowSelectionLabel } from '../../shared/tdb-row-label';
import { Testdatenblatt } from '../../models/testdatenblatt.model';
import { TdbOverride, bedingungFragment, formatTcdlPreview, tdbIdSegment } from '../../shared/tcdl-ops';

export interface TestdatenblattSummary {
  readonly id: string;
  readonly name: string;
  readonly rows: number;
  readonly spalten?: readonly string[];
  /** Aktiv-Zustand je Zeile (NEX-2262); fehlt er, sind alle Zeilen an. */
  readonly aktiv?: readonly boolean[];
}

export interface TestdatenBlockRow {
  readonly tdb: TestdatenblattSummary;
  readonly override: TdbOverride;
}

/** Ein Blatt, wie es der Block zeigt — die Kennung ist der Blatt-Key, nicht die UUID. */
export function toTestdatenblattSummary(sheet: Testdatenblatt): TestdatenblattSummary {
  return {
    id: sheet.key,
    name: sheet.name,
    rows: sheet.zeilen.length,
    spalten: sheet.spalten,
    aktiv: sheet.zeilenAktiv,
  };
}

/**
 * Ausdrucksfrei „was zeigt eine Zeile" — der Rest laeuft ueber die Translate-Pipe
 * im Template und bleibt Sprachwechseln gegenueber reaktiv, weil {@code rendered}
 * an {@code langChange} haengt.
 */
interface RenderedRow {
  readonly tdb: TestdatenblattSummary;
  readonly override: TdbOverride;
  readonly filter: string;
  readonly filterActive: boolean;
  readonly constrained: boolean;
  readonly rowsLabel: string;
  readonly preview: string;
  /** Nicht deutbare Teile der Zeilen-Auswahl, mit „, " verbunden; leer, wenn alles lesbar ist. */
  readonly unreadable: string;
  /** Zeilennummern jenseits des Blatts, mit „, " verbunden; leer, wenn es alle gibt. */
  readonly missingRows: string;
  readonly missingRowCount: number;
  /** Die Auswahl trifft Zeilen, aber nur ausgeschaltete — das Modul laeuft dann nicht. */
  readonly onlyInactive: boolean;
  readonly hasHint: boolean;
}

/**
 * Testdaten-Block eines Modulaufrufs: verbundene Datenblaetter, Zeilen-Auswahl,
 * Bedingung und TCDL-Vorschau.
 */
@Component({
  selector: 'app-testdaten-block',
  standalone: true,
  imports: [TranslatePipe, StepConditionEditorComponent],
  templateUrl: './testdaten-block.component.html',
  styleUrl: './testdaten-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestdatenBlockComponent {
  private static nextInstance = 0;
  /** Mehrere Bloecke auf einer Seite koennen dasselbe Blatt zeigen; die Warn-ID muss trotzdem eindeutig sein. */
  readonly instanceId = ++TestdatenBlockComponent.nextInstance;

  private readonly translate = inject(TranslateService);
  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly moduleTitle = input.required<string>();
  /**
   * Anzuzeigende Datenblatt-Zeilen. Der Block haelt keinen eigenen Zustand: Er
   * meldet jede Eingabe ueber {@link overrideChanged} bzw. {@link overrideReset}
   * und rendert erst neu, wenn der Aufrufer den gepatchten Stand zurueckgibt
   * (Echo-Vertrag). Die Liste ist darum {@code readonly} — sie gehoert dem Parent.
   */
  readonly rows = input.required<readonly TestdatenBlockRow[]>();
  /**
   * Optionaler Titel-i18n-Schluessel. Default: der testfall-scoped Titel
   * ({@code tdb.title.testcase}). Der Drawer im Sub-Modul setzt {@code tdb.title.module},
   * weil das Override dort modulglobal wirkt.
   */
  readonly titleKey = input<string>('tdb.title.testcase');
  /** Nur-Lesen: Filterfeld und Bedingung sind gesperrt. */
  readonly disabled = input(false);
  readonly showPreview = input(true);
  /** Im Testfall steht „Ausführen wenn“ als Liste über dem Modul; dort entfällt die Bedingung hier. */
  readonly showCondition = input(true);
  readonly unlinkable = input(false);

  readonly overrideChanged = output<{ tdbId: string; patch: Partial<TdbOverride> }>();
  readonly overrideReset = output<string>();
  readonly unlinkRequested = output<string>();

  readonly rendered = computed<RenderedRow[]>(() => {
    this.langChange();
    return this.rows().map((r) => this.renderRow(r));
  });

  private renderRow(entry: TestdatenBlockRow): RenderedRow {
    const ov = entry.override ?? {};
    const filter = (ov.filter ?? '').trim();
    const fragment = bedingungFragment(ov);
    const constrained = filter.length > 0 || fragment !== null;
    const preview = formatTcdlPreview(this.moduleTitle(), tdbIdSegment(entry.tdb.id, filter), fragment);
    const { rows, aktiv } = entry.tdb;
    const unreadable = parseFilterExpression(filter).warnings.join(', ');
    const missing = missingRowNumbers(rows, filter);
    const onlyInactive =
      filter.length > 0 &&
      countSelectedRows(rows, filter).count > 0 &&
      countSelectedRows(rows, filter, null, aktiv).count === 0;
    return {
      tdb: entry.tdb,
      override: ov,
      filter: ov.filter ?? '',
      filterActive: filter.length > 0,
      constrained,
      rowsLabel: constrained
        ? tdbRowSelectionLabel(this.translate, rows, filter, null, aktiv)
        : this.translate.instant('tdb.status.rowsAll', { count: activeRowCount(rows, aktiv) }),
      preview,
      unreadable,
      missingRows: missing.join(', '),
      missingRowCount: missing.length,
      onlyInactive,
      hasHint: !!unreadable || missing.length > 0 || onlyInactive,
    };
  }

  onFilterInput(tdbId: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.overrideChanged.emit({ tdbId, patch: { filter: value } });
  }

  onConditionChange(tdbId: string, patch: Partial<TdbOverride>): void {
    this.overrideChanged.emit({ tdbId, patch });
  }

  onReset(tdbId: string): void {
    this.overrideReset.emit(tdbId);
  }

  onUnlink(tdbId: string): void {
    this.unlinkRequested.emit(tdbId);
  }
}
